/**
 * Utility functions for extracting and structuring planner step data from chat messages.
 *
 * Message organisation is dictated by backend metadata, NEVER by regex on
 * message text. The planner orchestrator (backend-ai/Agent) stamps the
 * following onto each ChatMessage it persists:
 *   - metadata.planner_execution_plan : the plan-summary message
 *   - metadata.step_id (integer >=1)   : step-scoped messages (planner
 *                                        narration AND sub-agent output share
 *                                        the same step_id and are bucketed
 *                                        together under their step)
 *   - metadata.from_planner (bool)     : true on planner narration, absent/
 *                                        false on sub-agent output (used only
 *                                        for StepDetailPanel content filtering)
 * Step completion status is the backend's `steps_artifacts` (passed here as
 * stepsStatusFromArtifacts); live in-progress status is surfaced by the SSE
 * activeSteps overlay in PlannerStepsViewer. We never infer a step advance
 * from text markers (e.g. "**Analysis of the output outcome**") or a sequential
 * counter, which previously mis-attributed streaming output to Step 1.
 *
 * Legacy helpers (isPlanMessage / parseStepsFromPlanText) are retained as a
 * fallback for chats created before the backend stamped planner_execution_plan.
 */

/**
 * Check if message is from a real user (not planner/assistant)
 * @param {Object} message
 * @returns {boolean}
 */
export const isUserMessage = (message) => {
  const userId = message?.user?.id;
  // Real users have a UUID as id, not null or "planner"
  return (
    userId !== null &&
    userId !== "planner" &&
    message?.user?.name !== "assistant"
  );
};

/**
 * Check if message is a planner response to user (plan summary, acknowledgments)
 * @param {Object} message
 * @returns {boolean}
 */
export const isPlannerResponseToUser = (message) => {
  return message?.user?.id === null && message?.user?.name === "assistant";
};

/**
 * Check if message is planner-to-agent or agent response
 * @param {Object} message
 * @returns {boolean}
 */
export const isPlannerAgentMessage = (message) => {
  return message?.user?.id === "planner";
};

/**
 * Check if message contains a plan definition
 * @param {Object} message
 * @returns {boolean}
 */
export const isPlanMessage = (message) => {
  if (!isPlannerResponseToUser(message)) return false;
  const text = message?.text || "";
  return (
    text.includes("**Plan summary**") ||
    text.includes("**Plan details**") ||
    /\*\*Step\s+\d+\*\*\s*:/i.test(text)
  );
};

/**
 * Parse steps from plan message text
 * Format: "**Step N** : description\n**Assigned to** : agent name"
 * @param {string} text
 * @returns {Array<Object>}
 */
export const parseStepsFromPlanText = (text) => {
  if (!text) return [];

  const steps = [];

  // First, find all step positions
  const stepStartRegex = /\*\*Step\s+(\d+)\*\*\s*:/gi;
  const stepPositions = [];
  let startMatch;

  while ((startMatch = stepStartRegex.exec(text)) !== null) {
    stepPositions.push({
      index: startMatch.index,
      stepNumber: parseInt(startMatch[1], 10),
      fullMatch: startMatch[0],
      contentStart: startMatch.index + startMatch[0].length,
    });
  }

  // For each step, extract content until the next step or a section break
  for (let i = 0; i < stepPositions.length; i++) {
    const currentStep = stepPositions[i];
    const nextStepIndex =
      i + 1 < stepPositions.length ? stepPositions[i + 1].index : text.length;

    // Extract content between this step header and the next step (or end)
    let stepContent = text.substring(currentStep.contentStart, nextStepIndex);

    // Look for "Assigned to" within this step's content
    const assignedToMatch = stepContent.match(
      /\*\*Assigned to\*\*\s*:\s*([^\n*]+)/i
    );
    const assignedTo = assignedToMatch ? assignedToMatch[1].trim() : null;

    // Remove "Assigned to" line and everything after section breaks (---, emoji questions)
    let description = stepContent;

    // Remove "Assigned to" part
    if (assignedToMatch) {
      description = description.substring(0, assignedToMatch.index);
    }

    // Remove trailing section breaks and questions
    description = description
      .split(/\n---\n/)[0]
      .split(/\n\n🧑/)[0]
      .trim();

    // Clean up description
    description = description.replace(/\n+$/, "").trim();

    if (description) {
      const firstLine = description.split("\n")[0];
      const taskName = firstLine.length > 1000
        ? firstLine.substring(0, 1000) + "..."
        : firstLine;
      steps.push({
        stepIndex: currentStep.stepNumber - 1,
        stepNumber: currentStep.stepNumber,
        stepId: currentStep.stepNumber, // Use step number as stepId for lookup
        taskName,
        fullDescription: description,
        assignedTo,
        status: "pending",
      });
    }
  }

  return steps;
};

/**
 * Parse steps from planner_execution_plan metadata
 * @param {Array<Object>} executionPlan - Array of step objects from metadata
 * @returns {Array<Object>}
 */
const parseStepsFromExecutionPlan = (executionPlan) => {
  if (!executionPlan || !Array.isArray(executionPlan)) return [];

  return executionPlan.map((step, idx) => ({
    stepIndex: idx,
    stepNumber: step.step_id || idx + 1,
    stepId: step.step_id,
    agentId: step.agent_id,
    taskName: step.task_description?.split('\n')[0]?.substring(0, 1000) || `Step ${idx + 1}`,
    fullDescription: step.task_description || '',
    assignedTo: step.agent_name || step.agent_id,
    status: step.status || "pending",
    // Parallel execution fields - ONLY set if explicitly defined in plan
    // undefined = sequential (default), [] = can run immediately in parallel
    dependsOn: step.depends_on !== undefined ? step.depends_on : undefined,
    parallelGroup: step.parallel_group || null,
    allowParallel: step.allow_parallel !== false,
    authorisationRequired: step.authorisation_required || false,
    computerRequired: step.computer_required || false,
  }));
};

/**
 * Group steps into parallel execution waves based on dependencies
 * IMPORTANT: Steps without explicit depends_on are SEQUENTIAL by default
 * Only steps with explicit empty depends_on [] can run in parallel
 * @param {Array<Object>} steps - Array of step objects
 * @returns {Array<Object>} - Array of wave objects with steps
 */
export const groupStepsIntoWaves = (steps) => {
  if (!steps || steps.length === 0) return [];

  const waves = [];
  const completedStepIds = new Set();
  const remainingSteps = [...steps];

  while (remainingSteps.length > 0) {
    const wave = {
      waveIndex: waves.length,
      steps: [],
      isParallel: false,
    };

    // Find all steps whose dependencies are satisfied
    const readySteps = remainingSteps.filter(step => {
      // Check if step has EXPLICIT dependencies defined
      const hasExplicitDeps = step.dependsOn !== undefined && Array.isArray(step.dependsOn);

      if (hasExplicitDeps) {
        // Step has explicit dependencies - check if satisfied
        return step.dependsOn.every(depId => completedStepIds.has(depId));
      } else {
        // No explicit dependencies - DEFAULT TO SEQUENTIAL
        // This step depends on ALL previous steps being completed
        const previousStepIds = steps
          .filter(s => s.stepIndex < step.stepIndex)
          .map(s => s.stepId || s.stepNumber);
        return previousStepIds.every(id => completedStepIds.has(id));
      }
    });

    if (readySteps.length === 0) {
      // No steps ready - circular dependency or error, add remaining steps
      console.warn('[groupStepsIntoWaves] No ready steps, adding remaining:', remainingSteps);
      wave.steps = remainingSteps.map(s => s.stepIndex);
      wave.isParallel = remainingSteps.length > 1;
      waves.push(wave);
      break;
    }

    // Add ready steps to this wave
    wave.steps = readySteps.map(s => s.stepIndex);
    // Only parallel if multiple steps AND they have explicit empty depends_on
    wave.isParallel = readySteps.length > 1 && readySteps.every(s =>
      s.dependsOn !== undefined && Array.isArray(s.dependsOn)
    );

    // Mark steps as completed and remove from remaining
    readySteps.forEach(step => {
      completedStepIds.add(step.stepId || step.stepNumber);
      const idx = remainingSteps.findIndex(s => s.stepIndex === step.stepIndex);
      if (idx >= 0) remainingSteps.splice(idx, 1);
    });

    waves.push(wave);
  }

  return waves;
};

/**
 * Get dependency step names for display
 * @param {Object} step - Step object
 * @param {Array<Object>} allSteps - All steps array
 * @returns {Array<string>} - Array of dependency step descriptions
 */
export const getDependencyNames = (step, allSteps) => {
  if (!step.dependsOn || step.dependsOn.length === 0) return [];

  return step.dependsOn.map(depId => {
    const depStep = allSteps.find(s =>
      s.stepId === depId || s.stepNumber === depId
    );
    return depStep
      ? `Step ${depStep.stepNumber}: ${depStep.taskName?.substring(0, 50)}...`
      : `Step ${depId}`;
  });
};

/**
 * Safely parse metadata if it's a JSON string
 * @param {any} metadata - The metadata field which could be a string or object
 * @returns {Object} - Parsed metadata object
 */
const parseMetadata = (metadata) => {
  if (!metadata) return {};
  if (typeof metadata === 'string') {
    try {
      return JSON.parse(metadata);
    } catch (e) {
      console.warn('Failed to parse metadata:', e);
      return {};
    }
  }
  return metadata;
};

/**
 * Extract step_id from message metadata
 * @param {Object} msg - Chat message
 * @returns {string|number|null} - The step_id or null
 */
const getStepIdFromMessage = (msg) => {
  const metadata = parseMetadata(msg?.metadata);
  const callbackMetadata = parseMetadata(msg?.callbackMetadata);

  return metadata.step_id ??
         callbackMetadata.step_id ??
         metadata.stepId ??
         callbackMetadata.stepId ??
         null;
};

/**
 * Check if a message is from the planner orchestrator (not from user or sub-agent)
 * Uses the from_planner metadata field set by the planner backend
 * @param {Object} msg - Chat message
 * @returns {boolean} - True if message is from planner
 */
export const isFromPlanner = (msg) => {
  const metadata = parseMetadata(msg?.metadata);
  const callbackMetadata = parseMetadata(msg?.callbackMetadata);

  return metadata.from_planner === true ||
         callbackMetadata.from_planner === true;
};

/**
 * Extract and structure planner step data from chat messages.
 *
 * Message organisation is dictated ENTIRELY by backend metadata — never by
 * regex on message text. The backend stamps the following on each ChatMessage:
 *   - metadata.planner_execution_plan : marks the plan-summary message
 *   - metadata.step_id (integer >=1)   : a step-scoped message (planner
 *                                        narration OR sub-agent output)
 *   - metadata.from_planner (bool)     : true on planner narration, absent/
 *                                        false on sub-agent output
 *   - top-level agentID column         : == planner id for narration,
 *                                        == sub-agent id for output
 * Step completion status is the backend's `steps_artifacts` (passed as
 * stepsStatusFromArtifacts), the authoritative source. We never infer a step
 * advance from text markers or a sequential counter.
 *
 * @param {Array<Object>} messages - Array of chat messages
 * @param {string} planningExecution - Plan-level status from chat metadata
 * @param {Array<Object>} stepsStatusFromArtifacts - Authoritative step status from steps_artifacts metadata [{step_id, status, agent_id}]
 * @param {string} [plannerAgentId] - The planner orchestrator agent id (reserved for UI; not required for attribution)
 * @returns {Object} - Structured planner step data
 */
export const extractPlannerStepData = (
  messages,
  planningExecution = null,
  stepsStatusFromArtifacts = [],
  plannerAgentId = null,
) => {
  // Determine initial planning status from planningExecution param
  let initialPlanningStatus = "no_plan";
  if (planningExecution) {
    if (planningExecution === "plan_execution_completed") {
      initialPlanningStatus = "plan_completed";
    } else if (planningExecution === "planning_requires_approval") {
      initialPlanningStatus = "planning_requires_approval";
    } else {
      initialPlanningStatus = "plan_execution_in_progress";
    }
  }

  const result = {
    steps: [],
    userMessages: [],
    timeline: [],
    summary: {
      totalSteps: 0,
      completedSteps: 0,
      failedSteps: 0,
      skippedSteps: 0,
      pendingSteps: 0,
      inProgressSteps: 0,
      activeStepIndex: null,
      planningStatus: initialPlanningStatus,
      isExecuting: false,
      isPlanCompleted: planningExecution === "plan_execution_completed",
    },
  };

  if (!messages || !Array.isArray(messages) || messages.length === 0) {
    return result;
  }

  // Sort messages chronologically with a NORMALISED timestamp and a stable
  // index tiebreak. createdAt can arrive as epoch-seconds (streaming
  // placeholders), epoch-millis (DataStore rows), ISO strings, or be absent.
  // Treating any of those as `|| 0` previously pushed streaming/missing
  // timestamps to the front and reordered steps while streaming — the root
  // cause of sub-agent output being attributed to the wrong step live and then
  // "fixing itself" after a reload.
  const toTime = (val) => {
    if (val == null || val === "") return Number.MAX_SAFE_INTEGER;
    if (typeof val === "number") return val;
    const n = Number(val);
    if (Number.isFinite(n)) return n;
    const d = Date.parse(val);
    return Number.isFinite(d) ? d : Number.MAX_SAFE_INTEGER;
  };
  const sortedMessages = messages
    .map((m, idx) => ({ m, idx }))
    .sort((a, b) => {
      const ta = toTime(a.m.createdAt);
      const tb = toTime(b.m.createdAt);
      if (ta !== tb) return ta - tb;
      return a.idx - b.idx; // stable tiebreak preserves original order
    })
    .map((x) => x.m);

  // First pass: locate the plan-summary message and parse the plan.
  // Preferred source: metadata.planner_execution_plan (the backend's
  // authoritative plan definition, which carries depends_on etc.).
  // Legacy fallback: text-based plan parsing for chats created before the
  // backend stamped planner_execution_plan onto the message.
  let planSteps = [];
  let planMessageIndex = -1;
  const planApproved = planningExecution && planningExecution !== "planning_requires_approval";
  const planCompleted = planningExecution === "plan_execution_completed";
  let executionPlanFromMetadata = null;

  for (let i = 0; i < sortedMessages.length; i++) {
    const msg = sortedMessages[i];
    const metadata = parseMetadata(msg.metadata);
    if (metadata.planner_execution_plan) {
      try {
        const planData =
          typeof metadata.planner_execution_plan === "string"
            ? JSON.parse(metadata.planner_execution_plan)
            : metadata.planner_execution_plan;
        if (Array.isArray(planData) && planData.length > 0) {
          executionPlanFromMetadata = planData;
          planMessageIndex = i;
          result.summary.planningStatus = "planning_requires_approval";
          break; // first plan message wins
        }
      } catch (e) {
        console.warn("Failed to parse planner_execution_plan:", e);
      }
    }
  }

  // Legacy text fallback only if no metadata plan was found
  if (!executionPlanFromMetadata && planSteps.length === 0) {
    for (let i = 0; i < sortedMessages.length; i++) {
      const msg = sortedMessages[i];
      if (isPlanMessage(msg)) {
        planSteps = parseStepsFromPlanText(msg.text);
        planMessageIndex = i;
        result.summary.planningStatus = "planning_requires_approval";
        break;
      }
    }
  }

  // Plan-level lifecycle (approval / completion) comes from the backend's
  // planningExecution status, not from sniffing message text like "approved"
  // or "**Plan concluded**".
  // planApproved / planCompleted are no longer used to advance a sequential
  // step counter — step lifecycle is backend-driven (planningExecution +
  // stepsStatusFromArtifacts). Retained here for legibility of the legacy
  // fallback below; mark intentionally-unused to keep linters quiet.
  void planApproved;
  void planCompleted;

  // Use execution plan from metadata if available - ALWAYS prefer metadata over text parsing
  // because metadata includes critical fields like depends_on for parallel execution
  if (executionPlanFromMetadata) {
    planSteps = parseStepsFromExecutionPlan(executionPlanFromMetadata);
  }

  if (planSteps.length === 0) {
    return result;
  }

  // Initialize steps with basic info
  const stepsMap = new Map();
  planSteps.forEach((step, idx) => {
    stepsMap.set(idx, {
      id: `step-${idx}`,
      stepIndex: idx,
      stepNumber: step.stepNumber,
      stepId: step.stepId || null,
      agentId: step.agentId || null,
      taskName: step.taskName,
      fullDescription: step.fullDescription,
      assignedTo: step.assignedTo,
      status: "pending",
      isActive: false,
      isCompleted: false,
      isFailed: false,
      isSkipped: false,
      isPending: true,
      isConditional: false,
      error: null,
      messages: [],
      _messageIdSet: new Set(), // For deduplication on refresh
      startTime: null,
      endTime: null,
      // Parallel execution fields - preserve undefined for sequential default
      dependsOn: step.dependsOn,  // undefined = sequential, [] = parallel
      parallelGroup: step.parallelGroup || null,
      allowParallel: step.allowParallel !== false,
      authorisationRequired: step.authorisationRequired || false,
      computerRequired: step.computerRequired || false,
    });
  });

  // Second pass: attribute messages to steps ENTIRELY by backend metadata.
  // A message belongs to a step iff its metadata.step_id is a number that
  // matches one of the plan's step ids. Both planner narration and sub-agent
  // output carry the SAME step_id (they are both displayed under their step),
  // so we never need to distinguish them here for bucketing — that distinction
  // (metadata.from_planner) only matters for StepDetailPanel content filtering.
  //
  // Crucially, messages without a valid step_id (e.g. a streaming placeholder
  // that has not yet received its metadata, plan-level narration, or memory/
  // conclude messages) are NEVER attributed to a step. This removes the old
  // sequential-counter fallback that bucketed live streaming output into
  // Step 1 whenever a step succeeded via narration that lacked the literal
  // "**Analysis of the output outcome**" text marker.
  //
  // Step status (in_progress / completed / failed) is NOT derived here — it is
  // applied below from the backend's authoritative steps_artifacts
  // (stepsStatusFromArtifacts), with live in-progress status surfaced by the
  // SSE activeSteps overlay in PlannerStepsViewer.

  // Build map from step_id to step index for quick lookup.
  // Use String keys to handle number/string mismatches.
  const stepIdToIndexMap = new Map();
  planSteps.forEach((step, idx) => {
    if (step.stepId != null) {
      stepIdToIndexMap.set(String(step.stepId), idx);
    }
  });

  // Helper to add message with deduplication. A message that previously had
  // no step_id (streaming placeholder) and later gains one is added here for
  // the first time, so it lands on the CORRECT step rather than being pinned
  // to a wrong step by the dedup set.
  const addMessageToStep = (step, msg) => {
    const msgId = msg._id || msg.id;
    if (msgId && step._messageIdSet.has(msgId)) {
      // Update existing message content on refresh
      const idx = step.messages.findIndex(m => m.messageId === msgId);
      if (idx >= 0) {
        step.messages[idx] = createMessageEntry(msg);
      }
      return;
    }
    if (msgId) {
      step._messageIdSet.add(msgId);
    }
    step.messages.push(createMessageEntry(msg));
  };

  // Track the most recent step index seen before a real user message, so user
  // feedback can be anchored under the step it followed (for the steps panel).
  let lastStepIndexForUserMsg = -1;

  for (let i = planMessageIndex + 1; i < sortedMessages.length; i++) {
    const msg = sortedMessages[i];

    // Real user messages (approval/feedback) are surfaced under steps as user
    // messages, keyed to the step they followed. We do NOT detect "approved"
    // from text — approval is reflected via the backend's planningExecution.
    if (isUserMessage(msg)) {
      result.userMessages.push({
        messageId: msg._id,
        text: msg.text,
        createdAt: msg.createdAt,
        afterStepIndex: lastStepIndexForUserMsg,
        userId: msg.user?.id,
        userName: msg.user?.name,
        type: "feedback",
      });
      continue;
    }

    // Attribute by backend step_id only.
    const stepId = getStepIdFromMessage(msg);
    if (stepId == null) {
      // Plan-level narration (memory update, plan conclude, plan-generation
      // thinking, plan-level streaming). Not a step-scoped message — visible in
      // the main chat via shouldShowInChat, not bucketed into a step.
      continue;
    }
    const stepIndex = stepIdToIndexMap.get(String(stepId));
    if (stepIndex == null) {
      // step_id present but not a known plan step (e.g. sentinel 0 on the
      // plan-summary message). Leave unbucketed rather than guess.
      continue;
    }

    const step = stepsMap.get(stepIndex);
    if (step) {
      if (!step.startTime) step.startTime = msg.createdAt;
      addMessageToStep(step, msg);
      lastStepIndexForUserMsg = stepIndex;
    }
  }

  // Build final steps array (clean up internal sets)
  result.steps = Array.from(stepsMap.values()).map(step => {
    const { _messageIdSet, ...cleanStep } = step;
    return cleanStep;
  });

  // Apply authoritative step status from steps_artifacts metadata
  // This is the source of truth from the backend for step completion
  if (stepsStatusFromArtifacts && stepsStatusFromArtifacts.length > 0) {
    const statusMap = new Map();
    stepsStatusFromArtifacts.forEach(s => {
      if (s.step_id != null) {
        statusMap.set(String(s.step_id), s.status);
      }
    });

    result.steps = result.steps.map(step => {
      const stepId = step.stepId || step.stepNumber;
      const artifactStatus = statusMap.get(String(stepId));

      if (artifactStatus) {
        const isCompleted = artifactStatus === 'completed';
        const isFailed = artifactStatus === 'failed';
        const isSkipped = artifactStatus === 'skipped';
        const isInProgress = artifactStatus === 'in_progress' || artifactStatus === 'inprogress';

        return {
          ...step,
          status: isCompleted ? 'completed' : isFailed ? 'failed' : isSkipped ? 'skipped' : isInProgress ? 'in_progress' : step.status,
          isCompleted,
          isFailed,
          isSkipped,
          isActive: isInProgress,
          isPending: !isCompleted && !isFailed && !isSkipped && !isInProgress,
        };
      }
      return step;
    });
  }

  // When the backend has not yet reported step status via steps_artifacts
  // (e.g. mid-execution before the first artifact status update, or a freshly
  // approved plan), derive a provisional in-progress step from the bucketed
  // messages: the highest step that has received messages and is not already
  // completed/failed. This complements the SSE activeSteps overlay applied in
  // PlannerStepsViewer and keeps the steps panel from looking fully idle while
  // execution has visibly started on a step.
  const hasArtifactStatus =
    stepsStatusFromArtifacts && stepsStatusFromArtifacts.length > 0;
  if (
    !hasArtifactStatus &&
    (result.summary.planningStatus === "plan_execution_in_progress" ||
      result.summary.planningStatus === "planning_requires_approval")
  ) {
    for (let idx = result.steps.length - 1; idx >= 0; idx--) {
      const step = result.steps[idx];
      if (step.isCompleted || step.isFailed || step.isSkipped) continue;
      if (step.messages && step.messages.length > 0) {
        step.status = "in_progress";
        step.isActive = true;
        step.isPending = false;
        break;
      }
    }
  }

  // Calculate summary
  result.steps.forEach((step, idx) => {
    if (step.isCompleted) result.summary.completedSteps++;
    else if (step.isFailed) result.summary.failedSteps++;
    else if (step.isSkipped) result.summary.skippedSteps++;
    else if (step.isPending) result.summary.pendingSteps++;

    if (step.isActive) {
      result.summary.activeStepIndex = idx;
      result.summary.inProgressSteps++;
    }

    if (step.startTime) {
      result.timeline.push({
        type: "step",
        timestamp: step.startTime,
        data: step,
      });
    }
  });

  // Add user messages to timeline
  result.userMessages.forEach((um) => {
    result.timeline.push({
      type: "user_message",
      timestamp: um.createdAt,
      data: um,
    });
  });

  // Sort timeline
  result.timeline.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));

  result.summary.totalSteps = result.steps.length;
  result.summary.isExecuting =
    result.summary.inProgressSteps > 0 ||
    result.summary.planningStatus === "plan_execution_in_progress";

  // Add waves for parallel execution visualization
  result.waves = groupStepsIntoWaves(result.steps);
  result.summary.hasParallelWaves = result.waves.some(w => w.isParallel);
  result.summary.parallelStepsCount = result.waves.reduce(
    (count, wave) => count + (wave.isParallel ? wave.steps.length : 0),
    0
  );

  return result;
};

/**
 * Create a message entry for step association
 */
const createMessageEntry = (msg) => {
  return {
    messageId: msg._id,
    text: msg.text,
    createdAt: msg.createdAt,
    metadata: msg.metadata || {},
    agentName: msg.agentName,
    agentID: msg.agentID,
    images: msg.images,
    audios: msg.audios,
    videos: msg.videos,
    files: msg.files,
    callbackMetadata: msg.callbackMetadata,
  };
};

/**
 * Get progress percentage
 * @param {Object} plannerData
 * @returns {number} 0-100
 */
export const getProgressPercentage = (plannerData) => {
  if (!plannerData?.summary?.totalSteps) return 0;
  const { completedSteps, skippedSteps, totalSteps } = plannerData.summary;
  return Math.round(((completedSteps + skippedSteps) / totalSteps) * 100);
};

/**
 * Get user messages that occurred after a specific step
 * @param {Object} plannerData
 * @param {number} afterStepIndex
 * @returns {Array}
 */
export const getUserMessagesAfterStep = (plannerData, afterStepIndex) => {
  if (!plannerData?.userMessages) return [];
  return plannerData.userMessages.filter(
    (msg) => msg.afterStepIndex === afterStepIndex && msg.type === "feedback"
  );
};

/**
 * Get status icon configuration for a step
 * @param {string} status
 * @param {Object} currentTheme - Theme object from AppTheme.useTheme()
 * @returns {Object} - { iconKey, color, animated }
 */
export const getStatusIconConfig = (status, currentTheme) => {
  const configs = {
    completed: {
      iconKey: "check",
      color: currentTheme?.theme?.green || "#4CAF50",
    },
    failed: {
      iconKey: "warning",
      color: currentTheme?.theme?.red || "#F44336",
    },
    pending: {
      iconKey: "clock",
      color: currentTheme?.theme?.mediumGrey || "#757575",
    },
    in_progress: {
      iconKey: "progress",
      color: currentTheme?.theme?.primary || "#2196F3",
      animated: true,
    },
    skipped: {
      iconKey: "arrow_right",
      color: currentTheme?.theme?.font_color || "#9E9E9E",
    },
  };
  return configs[status] || configs.pending;
};

/**
 * Check if a message should be shown in the main chat (user + planner responses)
 * @param {Object} message
 * @returns {boolean}
 */
export const shouldShowInChat = (message) => {
  // Organisation is dictated by backend metadata, never by message text:
  //   - Real user messages           -> main chat
  //   - Plan-summary message          -> main chat (metadata.planner_execution_plan)
  //   - Plan-level narration / memory / conclude (no step_id, or step_id
  //     sentinel 0)                                                 -> main chat
  //   - Step-scoped messages (metadata.step_id >= 1)               -> steps panel only
  // This keeps the main chat to user messages + plan-level conversation while
  // every step-scoped planner narration / sub-agent output lives under its
  // step in the steps panel.
  if (isUserMessage(message)) return true;

  const metadata = parseMetadata(message?.metadata);

  // The plan-summary message carries step_id==0 in some backend versions
  // (active_step_id at emit), so check the plan marker explicitly.
  if (metadata?.planner_execution_plan) return true;

  const stepId = getStepIdFromMessage(message);
  if (stepId == null || stepId === 0 || stepId === "0") {
    // Plan-level narration, memory/conclude, or plan-generation streaming
    // narration that has not yet been assigned a step. Visible in main chat.
    return true;
  }

  // Has a real step id -> belongs to the steps panel, not the main chat.
  return false;
};

/**
 * Check if a message should be shown in the steps panel (agent execution)
 * @param {Object} message
 * @returns {boolean}
 */
export const shouldShowInStepsPanel = (message) => {
  // Show planner-to-agent and agent responses
  return isPlannerAgentMessage(message);
};

export { getStepIdFromMessage };

/**
 * Extract all artifacts from messages (images, files, audios, videos)
 * This is the source of truth - messages are immutable, so artifacts are never lost
 * Includes: message-level arrays AND metadata fields (exec_result, generated_images, etc.)
 * @param {Array} messages - All chat messages
 * @returns {Array} - Accumulated artifacts with metadata
 */
export const extractAllArtifactsFromMessages = (messages) => {
  const artifactsMap = new Map(); // Dedupe by file_key

  messages?.forEach((msg) => {
    const metadata = parseMetadata(msg?.metadata);
    const stepId = metadata?.step_id;

    const addArtifacts = (items, type) => {
      if (!items || !Array.isArray(items)) return;
      items.forEach((key) => {
        // Skip internal step files and already seen keys
        if (key && !artifactsMap.has(key) && !key.includes('_internal_step')) {
          artifactsMap.set(key, {
            file_key: key,
            file_type: type,
            messageId: msg._id || msg.id,
            createdAt: msg.createdAt,
            agentName: msg.agentName || msg.user?.agentName,
            stepId: stepId,
          });
        }
      });
    };

    // 1. Message-level artifact arrays
    addArtifacts(msg.images, "image");
    addArtifacts(msg.files, "file");
    addArtifacts(msg.audios, "audio");
    addArtifacts(msg.videos, "video");

    // 2. Metadata exec_result output files (code execution outputs)
    if (metadata?.exec_result) {
      try {
        const execResult = typeof metadata.exec_result === 'string'
          ? JSON.parse(metadata.exec_result)
          : metadata.exec_result;
        addArtifacts(execResult?.output_files, "file");
      } catch (e) {
        // Ignore parse errors
      }
    }

    // 3. Metadata generated_images (image generation outputs)
    if (metadata?.generated_images) {
      const genImages = Array.isArray(metadata.generated_images)
        ? metadata.generated_images.flat()
        : [];
      addArtifacts(genImages, "image");
    }

    // 4. Metadata generated_videos (video generation outputs)
    if (metadata?.generated_videos) {
      const genVideos = Array.isArray(metadata.generated_videos)
        ? metadata.generated_videos.flat()
        : [];
      addArtifacts(genVideos, "video");
    }

    // 5. Metadata generated_models (3D model outputs)
    if (metadata?.generated_models) {
      const genModels = Array.isArray(metadata.generated_models)
        ? metadata.generated_models.flat()
        : [];
      addArtifacts(genModels, "model");
    }

    // 6. Metadata browser_recordings (browser/desktop session GIFs)
    // Each browse_web/control_desktop session appends its GIF here; older
    // sessions are preserved (unlike msg.files which can be replaced on SSE).
    if (metadata?.browser_recordings) {
      const recordings = Array.isArray(metadata.browser_recordings)
        ? metadata.browser_recordings.flat()
        : [];
      addArtifacts(recordings, "video");
    }
  });

  return Array.from(artifactsMap.values());
};

export default {
  extractPlannerStepData,
  getProgressPercentage,
  getUserMessagesAfterStep,
  getStatusIconConfig,
  isUserMessage,
  isPlannerResponseToUser,
  isPlannerAgentMessage,
  isPlanMessage,
  parseStepsFromPlanText,
  shouldShowInChat,
  shouldShowInStepsPanel,
  getStepIdFromMessage,
  groupStepsIntoWaves,
  getDependencyNames,
  extractAllArtifactsFromMessages,
  isFromPlanner,
};
